import { useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SrIcon } from "@/components/ui/SrIcon";
import { useUsers } from "@/hooks/use-users";
import { useMarkProjectCommentsRead } from "@/hooks/use-project-comment-reads";
import { ProjectUpdatesPanel } from "./ProjectUpdatesPanel";
import { OpenInChatLink } from "./OpenInChatLink";

interface ProjectUpdatesDialogProps {
  projectId: string;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * VIB-2179: hele prosjektoppdaterings-panelet (tråder, likes, vedlegg,
 * @-tagging) som popup fra /prosjekter og /mine-oppgaver.
 *
 * Markerer prosjektet som lest for aktiv bruker både ved åpning og lukking:
 * åpning nullstiller badgen med én gang, lukking fanger oppdateringer som
 * kom inn (realtime) mens dialogen sto åpen.
 */
export function ProjectUpdatesDialog({ projectId, projectName, open, onOpenChange }: ProjectUpdatesDialogProps) {
  const { data: allUsers = [] } = useUsers();
  const { mutate: markRead } = useMarkProjectCommentsRead();

  useEffect(() => {
    if (!open) return;
    markRead({ projectId });
    return () => {
      markRead({ projectId });
    };
  }, [open, projectId, markRead]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[80vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-4 py-3 border-b text-left">
          <DialogTitle className="flex items-center gap-2 text-base">
            <SrIcon name="message-square" className="w-5 h-5 text-primary" />
            Prosjektoppdateringer — {projectName}
            <OpenInChatLink projectId={projectId} className="ml-auto mr-8" />
          </DialogTitle>
          <DialogDescription className="sr-only">
            Oppdateringer, kommentarer og vedlegg for prosjektet {projectName}.
          </DialogDescription>
        </DialogHeader>
        {/* Samme brukerliste som prosjektsiden (ProjectDetailLayout → useUsers). */}
        <ProjectUpdatesPanel projectId={projectId} users={allUsers} />
      </DialogContent>
    </Dialog>
  );
}

export default ProjectUpdatesDialog;
